import { useTenant, useAnalytics } from '@parcel2go/npm-p2g-app';
import { ParcelValidationResponse } from '~/lib/types/edge-api-types';
import useSWR from 'swr';
import { EDGE_API } from '~/lib/constants';
import useQuote from './use-quote';
import useBasketSection from './use-basket-section';
import { QuoteAddress } from '~/lib/types/basket-types';
import { BasketSections } from '~/lib/types/enums';
import { useMemo } from 'react';
import { fetchWithTenantAnalytics } from '~/lib/fetch';
import { capturePromiseException } from '~/lib/metrics';

type UseParcelCategoryParams = {
  contents: string | undefined | null;
};

export default function useParcelValidation({ contents }: UseParcelCategoryParams) {
  const destination = useBasketSection<QuoteAddress>({ name: BasketSections.DESTINATION });
  const { session } = useAnalytics();
  const { quote } = useQuote();
  const { tenant, settings } = useTenant();

  const { data, isLoading } = useSWR<ParcelValidationResponse>(
    contents && quote && contents.length > 2 && destination?.data?.country
      ? `${EDGE_API}/${tenant}/order-process/parcel-category/validate?contents=${encodeURIComponent(contents)}&courier=${encodeURIComponent(quote.service.courierSlug)}&service=${encodeURIComponent(quote.service.slug)}&country=${encodeURIComponent(destination.data.country)}`
      : null,
    (url: string) =>
      fetchWithTenantAnalytics(tenant, settings, session, url)
        .then((res) => res.json())
        .catch(capturePromiseException),
    { revalidateOnFocus: false, revalidateOnReconnect: false, revalidateIfStale: true }
  );

  const matches = useMemo(() => (!isLoading && data?.result?.length ? data.result : []), [data, isLoading]);

  const isProhibited = useMemo(() => matches.some((x) => x.matches.some((y) => y.type === 'Prohibited')), [matches]);

  const isNoProtection = useMemo(
    () => matches.some((x) => x.matches.some((y) => y.type === 'No Compensation')),
    [matches]
  );

  const isMoreInformation = useMemo(
    () => matches.some((x) => x.matches.some((y) => y.type === 'More Information')),
    [matches]
  );

  const isConditional = useMemo(
    () => matches.some((x) => x.matches.some((y) => y.type === 'Conditional Cariage')),
    [matches]
  );

  return {
    type: isProhibited
      ? 'Prohibited'
      : isNoProtection
        ? 'No Compensation'
        : isMoreInformation
          ? 'More Information'
          : isConditional
            ? 'Conditional Cariage'
            : undefined,
    conditional: isConditional,
    prohibited: isProhibited,
    noProtection: isNoProtection,
    moreInformation: isMoreInformation,
    matches: matches,
    match: matches[0],
    loading: isLoading
  };
}
